import React, { useEffect, useState } from 'react';
import { analyticsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Shield, DollarSign, TrendingUp, AlertCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Link } from 'react-router-dom';
import OnboardingTour from '../components/OnboardingTour';
import ReferralProgram from '../components/ReferralProgram';
import { useTranslation } from '../hooks/useTranslation';

const OverviewPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    loadData();
    checkOnboarding();
  }, []);

  const checkOnboarding = () => {
    const completed = localStorage.getItem('onboarding_completed');
    if (!completed) {
      setShowOnboarding(true);
    }
  };

  const loadData = async () => {
    try {
      const response = await analyticsAPI.getOverview();
      setData(response.data);
    } catch (error) {
      console.error('Failed to load overview', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showOnboarding && (
        <OnboardingTour 
          user={user} 
          onComplete={() => setShowOnboarding(false)} 
        />
      )}

      <div className="space-y-8" data-testid="overview-page">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              Bonjour, {data?.user?.full_name?.split(' ')[0] || 'là'} !
            </h1>
            <p className="text-slate-600 mt-1">Voici un aperçu de votre activité</p>
          </div>
          <div className="flex space-x-3">
            <Link to="/dashboard/smartfacture/create">
              <Button className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white">
                <FileText className="h-4 w-4 mr-2" />
                Nouvelle facture
              </Button>
            </Link>
          </div>
        </div>

        {/* Plan Alert */}
        <Alert className="bg-gradient-to-r from-blue-50 to-violet-50 border-blue-200">
          <Sparkles className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-slate-700">
            Vous êtes sur le plan <strong className="text-blue-600">{data?.subscription?.smartfacture?.plan || 'Gratuit'}</strong>. 
            <Link to="/pricing" className="ml-2 text-blue-600 hover:text-blue-700 font-medium underline">
              Passer au plan Pro
            </Link>
          </AlertDescription>
        </Alert>

        {/* Stats Grid */}
        <div className="grid gap-4 md:gap-6 grid-cols-2 lg:grid-cols-4">
          <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-smartfacture-invoices">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Factures</CardTitle>
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-4 w-4 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">{data?.smartfacture?.total_invoices || 0}</div>
              <p className="text-xs text-green-600 mt-1">
                {data?.smartfacture?.paid_invoices || 0} payées
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">En attente</CardTitle>
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText className="h-4 w-4 text-amber-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{(data?.smartfacture?.total_invoices || 0) - (data?.smartfacture?.paid_invoices || 0)}</div>
              <p className="text-xs text-slate-500 mt-1">
                factures à encaisser
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Objectif MRR</CardTitle>
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-4 w-4 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">€5-9K</div>
              <p className="text-xs text-slate-500 mt-1">Cible mensuelle</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Conformité</CardTitle>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Shield className="h-4 w-4 text-emerald-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">100%</div>
              <p className="text-xs text-slate-500 mt-1">Factur-X 2026</p>
            </CardContent>
          </Card>
        </div>

        {/* SmartFacture AI Overview - Full Width */}
        <Card className="border-slate-200 hover:shadow-lg transition-shadow group" data-testid="smartfacture-overview">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl shadow-lg shadow-blue-500/20">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl" style={{fontFamily: "'Outfit', sans-serif"}}>SmartFacture AI</CardTitle>
                  <CardDescription>Votre solution de facturation intelligente</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-slate-800" style={{fontFamily: "'Outfit', sans-serif"}}>{data?.smartfacture?.total_invoices || 0}</p>
                <p className="text-sm text-slate-500 mt-1">Factures créées</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-emerald-600" style={{fontFamily: "'Outfit', sans-serif"}}>{data?.smartfacture?.paid_invoices || 0}</p>
                <p className="text-sm text-slate-500 mt-1">Payées</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-amber-600" style={{fontFamily: "'Outfit', sans-serif"}}>{(data?.smartfacture?.total_invoices || 0) - (data?.smartfacture?.paid_invoices || 0)}</p>
                <p className="text-sm text-slate-500 mt-1">En attente</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-blue-600" style={{fontFamily: "'Outfit', sans-serif"}}>100%</p>
                <p className="text-sm text-slate-500 mt-1">Conformité 2026</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/dashboard/smartfacture">
                <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl shadow-md shadow-blue-500/20">
                  Voir le dashboard
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/dashboard/smartfacture/create">
                <Button variant="outline" className="rounded-xl border-blue-200 text-blue-600 hover:bg-blue-50">
                  Créer une facture
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Actions rapides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/dashboard/smartfacture/create">
                <Button variant="outline" className="w-full h-auto py-4 flex-col space-y-2 hover:bg-blue-50 hover:border-blue-300">
                  <FileText className="h-6 w-6 text-blue-600" />
                  <span className="text-sm">Créer facture</span>
                </Button>
              </Link>
              <Link to="/dashboard/localmod">
                <Button variant="outline" className="w-full h-auto py-4 flex-col space-y-2 hover:bg-violet-50 hover:border-violet-300">
                  <Shield className="h-6 w-6 text-violet-600" />
                  <span className="text-sm">Modérer contenu</span>
                </Button>
              </Link>
              <Link to="/dashboard/metrics">
                <Button variant="outline" className="w-full h-auto py-4 flex-col space-y-2 hover:bg-green-50 hover:border-green-300">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                  <span className="text-sm">Voir métriques</span>
                </Button>
              </Link>
              <Link to="/dashboard/profile">
                <Button variant="outline" className="w-full h-auto py-4 flex-col space-y-2 hover:bg-amber-50 hover:border-amber-300">
                  <Sparkles className="h-6 w-6 text-amber-600" />
                  <span className="text-sm">Mon profil</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Referral Program Widget */}
        <ReferralProgram variant="widget" />
      </div>
    </>
  );
};

export default OverviewPage;
